"use client"

import * as React from "react"

/**
 * Places the open menu so the current choice sits on the field, the way a
 * select does, instead of docking the panel above or below the trigger.
 */
export function useAlignMenuToSelection(
  open: boolean,
  contentRef: React.RefObject<HTMLElement | null>,
  triggerRef: React.RefObject<HTMLElement | null>,
) {
  React.useLayoutEffect(() => {
    if (!open) return
    // Captured now: by cleanup time the ref may point at another node,
    // and the styles have to come off the one we put them on.
    const content = contentRef.current

    let scrolled = false
    let frames = 0
    let frame = 0
    let observer: MutationObserver | null = null

    const align = () => {
      const content = contentRef.current
      const popper = content?.parentElement
      const trigger = triggerRef.current
      if (!content || !popper || !trigger) return false
      const selected = content.querySelector<HTMLElement>(
        '[aria-selected="true"], [aria-current="true"]',
      )
      if (!selected) return false
      const scroller = selected.closest<HTMLElement>(
        "[data-slot='category-picker-panel'], [data-slot='folder-picker-panel']",
      )
      if (scroller && !scrolled) {
        const scrollerBox = scroller.getBoundingClientRect()
        const selectedBox = selected.getBoundingClientRect()
        scroller.scrollTop +=
          selectedBox.top -
          scrollerBox.top -
          scroller.clientHeight / 2 +
          selected.clientHeight / 2
        scrolled = true
      }
      const row = selected.getBoundingClientRect()
      const field = trigger.getBoundingClientRect()
      const dy = field.top - row.top
      const dx = field.right - row.right
      if (Math.abs(dy) >= 0.5 || Math.abs(dx) >= 0.5) {
        const currentTop = Number.parseFloat(popper.style.marginTop || "0")
        const currentLeft = Number.parseFloat(popper.style.marginLeft || "0")
        popper.style.marginTop = `${currentTop + dy}px`
        popper.style.marginLeft = `${currentLeft + dx}px`
      }
      content.style.borderRadius = getComputedStyle(trigger).borderRadius
      if (!observer) {
        observer = new MutationObserver(() => {
          align()
        })
        observer.observe(popper, { attributes: true, attributeFilter: ["style"] })
      }
      return true
    }

    const loop = () => {
      align()
      frames += 1
      if (frames < 20) frame = requestAnimationFrame(loop)
    }
    frame = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(frame)
      observer?.disconnect()
      const popper = content?.parentElement
      if (popper) {
        popper.style.marginTop = ""
        popper.style.marginLeft = ""
      }
    }
  }, [contentRef, open, triggerRef])
}
